import React, { createRef, useCallback, useEffect, useRef, useState } from 'react';
import { KeyboardType, StyleSheet, Text, View } from 'react-native';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Picker, { EmojiClickData } from 'emoji-picker-react';
import FontAwesome6 from 'react-native-vector-icons/FontAwesome6';
import { t } from 'i18next';
import { DARK_GRAY, PRIMARY_COLOR } from '../../styles/appColor';
import { AIModal } from '../molecules/AIModal';
import { useMutation } from '@tanstack/react-query';
import { generateLLM } from '../../services/api/llm.api';
import useLoggedUser from '../../hooks/useLoggedUser';
import { Chip } from './Chip';
import { setLoading } from '../../store/action';
import { useDispatch } from 'react-redux';

export const colors = [
  '#000000',
  '#e60000',
  '#ff9900',
  '#ffff00',
  '#008a00',
  '#0066cc',
  '#9933ff',
  '#ffffff',
  '#facccc',
  '#ffebcc',
  '#ffffcc',
  '#cce8cc',
  '#cce0f5',
  '#ebd6ff',
  '#bbbbbb',
  '#f06666',
  '#ffc266',
  '#ffff66',
  '#66b966',
  '#66a3e0',
  '#c285ff',
  '#888888',
  '#a10000',
  '#b26b00',
  '#b2b200',
  '#006100',
  '#0047b2',
  '#6b24b2',
  '#444444',
  '#5c0000',
  '#663d00',
  '#666600',
  '#003700',
  '#002966',
  '#3d1466',
];

var Link = Quill.import('formats/link');
var builtInFunc = Link.sanitize;
Link.sanitize = function customSanitizeLinkInput(linkValueInput: string) {
  let PROTOCOL_WHITELIST = ['http:', 'https:', 'mailto:'];
  for (let i = 0; i < PROTOCOL_WHITELIST.length; i++) {
    if (linkValueInput.startsWith(PROTOCOL_WHITELIST[i])) {
      return builtInFunc.call(this, linkValueInput);
    }
  }

  return builtInFunc.call(this, `https://${linkValueInput}`);
};

let id = 0;

const getId = (): number => {
  id += 1;
  return id;
};

export function AppRTEEditor(props: {
  maxLength?: number;
  displayOnly?: boolean;
  style: any;
  onSubmitEditing?: any;
  title: any;
  defaultValue: string;
  onChangeText: any;
  keyboardType?: KeyboardType;
  secureTextEntry?: boolean;
}) {
  const loggedUser = useLoggedUser();
  const reactQuill = createRef<ReactQuill>();
  const containerRef = useRef<View>(null);
  const dispatch = useDispatch();
  const [openEmoji, setOpenEmoji] = useState(false);
  const [displayAIModal, setDisplayAIModal] = useState(false);
  const [hasUsedAi, setHasUsedAi] = useState(false);
  const [id] = useState(getId().toString());

  // Refresh after 100ms
  // Sometimes, the quill editor is not initialized correctly, so we refresh it
  const [, setRefresh] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      setRefresh(true);
    }, 100);

    return () => clearInterval(interval);
  }, []);

  const onEmojiClick = useCallback(
    (data: EmojiClickData) => {
      if (!reactQuill.current) {
        return;
      }
      const quill = reactQuill.current.getEditor();
      if (!quill) {
        return null;
      }
      const position = quill.getSelection()?.index ?? quill.getLength() - 1;

      quill.insertText(position, data.emoji, 'silent');
      setOpenEmoji(false);
    },
    [reactQuill]
  );

  const Toolbar = useCallback(
    () => (
      <div id={'toolbar-' + id}>
        <button className="ql-bold" />
        <button className="ql-italic" />
        <button className="ql-link" />
        <select className="ql-color">
          {colors.map((color) => (
            <option value={color} key={color} />
          ))}
        </select>
        <button onClick={() => setOpenEmoji((prev) => !prev)}>🙂</button>
        <button onClick={() => setDisplayAIModal((prev) => !prev)} style={styles.aiButton}>
          <View style={styles.aiButtonView}>
            <FontAwesome6 name="brain" size={12} />
            <Text style={styles.aiButtonLabel}>{t('ai')}</Text>
          </View>
        </button>
      </div>
    ),
    [id, setOpenEmoji]
  );

  const { mutate: autocompleteWithAi, isLoading: isLoadingAi } = useMutation({
    mutationFn: (prompt: string) =>
      generateLLM(loggedUser, {
        messages: [
          {
            role: 'system',
            content: t('preprompt_no_introduction_or_ending'),
          },
          {
            role: 'user',
            content: props.defaultValue,
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
      }),
    onSuccess: (response) => {
      props.onChangeText(response.content);
    },
  });

  useEffect(() => {
    dispatch(setLoading(isLoadingAi));
  }, [dispatch, isLoadingAi]);

  return (
    <View style={styles.container} ref={containerRef}>
      {openEmoji && <Picker onEmojiClick={onEmojiClick} />}
      {displayAIModal && (
        <AIModal
          isVisible={displayAIModal}
          onGenerated={(value) => {
            props.onChangeText(value);
            setHasUsedAi(true);
          }}
          onClose={() => setDisplayAIModal(false)}
        />
      )}
      <Toolbar />
      {containerRef.current && (
        <ReactQuill
          ref={reactQuill}
          formats={['bold', 'italic', 'link', 'color']}
          theme="snow"
          bounds={containerRef.current}
          modules={{
            toolbar: {
              container: '#toolbar-' + id,
            },
          }}
          onFocus={() => setOpenEmoji(false)}
          value={props.defaultValue ?? ''}
          onChange={props.onChangeText}
          style={styles.quill}
        />
      )}
      {hasUsedAi && (
        <View style={styles.suggestionsContainer}>
          <Chip
            text={t('shorter')}
            color={PRIMARY_COLOR}
            onPress={() => autocompleteWithAi(t('shorter_prompt'))}
            disable={isLoadingAi}
            style={isLoadingAi ? styles.opacityHalf : undefined}
            small
          />
          <Chip
            text={t('more_long')}
            color={PRIMARY_COLOR}
            onPress={() => autocompleteWithAi(t('more_long_prompt'))}
            disable={isLoadingAi}
            style={isLoadingAi ? styles.opacityHalf : undefined}
            small
          />
          <Chip
            text={t('more_formal')}
            color={PRIMARY_COLOR}
            onPress={() => autocompleteWithAi(t('more_formal_prompt'))}
            disable={isLoadingAi}
            style={isLoadingAi ? styles.opacityHalf : undefined}
            small
          />
          <Chip
            text={t('less_formal')}
            color={PRIMARY_COLOR}
            onPress={() => autocompleteWithAi(t('less_formal_prompt'))}
            disable={isLoadingAi}
            style={isLoadingAi ? styles.opacityHalf : undefined}
            small
          />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, minHeight: 400, marginBottom: 100 },
  quill: { minHeight: 300 },
  aiButton: { width: 'auto', fontSize: 12, alignItems: 'center', display: 'flex' },
  aiButtonView: { flexDirection: 'row', borderWidth: 1, borderColor: DARK_GRAY, borderRadius: 4, padding: 4, gap: 4 },
  aiButtonLabel: { color: DARK_GRAY, fontSize: 12, alignItems: 'center' },
  suggestionsContainer: {
    flexDirection: 'row',
    gap: 10,
    flexWrap: 'wrap',
    marginTop: 20,
    justifyContent: 'center',
  },
  opacityHalf: {
    opacity: 0.5,
  },
});
